/** Network data - with device/browser info */
export interface NetworkData {
  // Connection quality
  strength: "excellent" | "good" | "fair" | "poor" | "offline";
  downloadMbps?: number; // download speed
  rttMs?: number; // latency
  effectiveType?: string; // '4g' | '3g' | '2g' | 'slow-2g'
  connectionType?: string; // 'wifi' | 'cellular' | 'ethernet'
  // Device info
  deviceType: "desktop" | "mobile" | "tablet";
  os: string; // 'Windows' | 'macOS' | 'iOS' | 'Android' | 'Linux'
  browser: string; // 'Chrome' | 'Firefox' | 'Safari' | 'Edge'
  browserVersion: string;
}

/** Detect device type from user agent */
const getDeviceType = (): "desktop" | "mobile" | "tablet" => {
  const ua = navigator.userAgent.toLowerCase();
  if (/tablet|ipad|playbook|silk/i.test(ua)) return "tablet";
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua))
    return "mobile";
  return "desktop";
};

/** Detect OS from user agent */
const getOS = (): string => {
  const ua = navigator.userAgent;
  if (/Windows/i.test(ua)) return "Windows";
  if (/Mac OS X|macOS/i.test(ua)) return "macOS";
  if (/iPhone|iPad|iPod/i.test(ua)) return "iOS";
  if (/Android/i.test(ua)) return "Android";
  if (/Linux/i.test(ua)) return "Linux";
  return "Unknown";
};

/** Detect browser and version */
const getBrowserInfo = (): { browser: string; version: string } => {
  const ua = navigator.userAgent;
  let browser = "Unknown";
  let version = "";

  if (/Edg\//i.test(ua)) {
    browser = "Edge";
    version = ua.match(/Edg\/(\d+)/)?.[1] || "";
  } else if (/Chrome/i.test(ua)) {
    browser = "Chrome";
    version = ua.match(/Chrome\/(\d+)/)?.[1] || "";
  } else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) {
    browser = "Safari";
    version = ua.match(/Version\/(\d+)/)?.[1] || "";
  } else if (/Firefox/i.test(ua)) {
    browser = "Firefox";
    version = ua.match(/Firefox\/(\d+)/)?.[1] || "";
  }

  return { browser, version };
};

/** Calculate connectivity strength */
const getConnectivityStrength = ():
  | "excellent"
  | "good"
  | "fair"
  | "poor"
  | "offline" => {
  if (!navigator.onLine) return "offline";

  const nav = navigator as any;
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
  if (!conn) return "good";

  const { effectiveType, rtt, downlink } = conn;

  if (effectiveType === "slow-2g" || effectiveType === "2g") return "poor";
  if (effectiveType === "3g") return "fair";
  if (effectiveType === "4g") {
    if (rtt > 500 || downlink < 0.5) return "poor";
    if (rtt > 300 || downlink < 1) return "fair";
    if (rtt < 100 && downlink > 5) return "excellent";
  }

  return "good";
};

/** Collect network data */
export const getNetworkData = (): NetworkData => {
  const nav = navigator as any;
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
  const { browser, version } = getBrowserInfo();

  return {
    strength: getConnectivityStrength(),
    downloadMbps: conn?.downlink,
    rttMs: conn?.rtt,
    effectiveType: conn?.effectiveType,
    connectionType: conn?.type,
    deviceType: getDeviceType(),
    os: getOS(),
    browser,
    browserVersion: version,
  };
};
